import { FC, lazy, Suspense, useEffect, useState } from "react";

import { useGetUserDetailsQuery } from "@store/services/user";
import { RootState } from "@store/rootReducer";
import { useSelector, shallowEqual } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { getStorageItem } from "@testavivadk/utilities";

import { authTokenName } from "@constants";

import { REFERENCE_AUTH_MODAL_STORAGE_KEY } from "./components/ReferenceAuthenticationModal/ReferenceAuthenticationModal.types";

const PartnersModal = lazy(() => import("./components/PartnersModal"));
const IdentityVerificationModal = lazy(() => import("./components/IdentityVerificationModal"));
const AuthenticationModal = lazy(() => import("./components/AuthenticationModal"));
const ConditionsModal = lazy(() => import("./components/ConditionsModal"));
const ReferenceAuthenticationModal = lazy(() => import("./components/ReferenceAuthenticationModal"));
const DownloadBlobModal = lazy(() => import("./components/DownloadBlobModal"));
const LandRegistrationModal = lazy(() => import("./components/DocumentModals/LandRegistration"));
const DigitalSignatureModal = lazy(() => import("./components/DocumentModals/DigitalSignature"));
const DocumentSignatureMethodSelectionModal = lazy(() => import("./components/DocumentModals/WillSignature"));

enum OnLoadModals {
  CONDITIONS = "CONDITIONS",
  REFERENCE_AUTHENTICATION = "REFERENCE_AUTHENTICATION",
}

const Modals: FC = () => {
  const [searchParameters] = useSearchParams();
  const {
    isPartnersModalOpen,
    identityVerificationModalState,
    authenticationModalData,
    downloadBlobData,
    isLandRegistrationModalOpen,
    isDocumentDigitalRegistrationModalOpen,
    digitalRegistrationDocument,
    isDocumentSignatureMethodSelectionModalOpen,
  } = useSelector(
    (state: RootState) => ({
      isPartnersModalOpen: state.modal.isPartnersModalOpen,
      identityVerificationModalState: state.modal.identityVerificationModalState,
      authenticationModalData: state.modal.authenticationModal,
      downloadBlobData: state.modal.downloadBlobData,
      isLandRegistrationModalOpen: state.modal.landRegistrationModalState.isOpen,
      isDocumentDigitalRegistrationModalOpen: state.modal.documentDigitalRegistrationModalState.isOpen,
      digitalRegistrationDocument: state.modal.documentDigitalRegistrationModalState.document,
      isDocumentSignatureMethodSelectionModalOpen: state.modal.documentSignatureMethodSelectionModalState.isOpen,
    }),
    shallowEqual,
  );

  const referenceAuthenticationToken = searchParameters.get(authTokenName);

  const [visibleOnLoadModal, setVisibleOnLoadModal] = useState<OnLoadModals | null>(null);
  const { data: userDetailsData, refetch: refetchUserDetails } = useGetUserDetailsQuery();

  const onCloseReferenceAuthenticationModal = () => {
    setVisibleOnLoadModal(null);
    refetchUserDetails();
  };

  useEffect(() => {
    if (userDetailsData?.authenticated) {
      getVisibleOnLoadModal();
    }
  }, [userDetailsData]);

  return (
    <Suspense fallback="">
      {isPartnersModalOpen ? <PartnersModal /> : null}
      {identityVerificationModalState.isOpen ? (
        <IdentityVerificationModal redirectUrl={identityVerificationModalState.redirectUrl} />
      ) : null}
      {authenticationModalData == null ? null : <AuthenticationModal data={authenticationModalData} />}
      {visibleOnLoadModal === OnLoadModals.CONDITIONS ? (
        <ConditionsModal
          previouslyConditionsAccepted={userDetailsData?.previouslyConditionsAccepted}
          onClose={() => setVisibleOnLoadModal(null)}
        />
      ) : null}
      {visibleOnLoadModal === OnLoadModals.REFERENCE_AUTHENTICATION &&
      referenceAuthenticationToken != null &&
      userDetailsData != null ? (
        <ReferenceAuthenticationModal
          token={referenceAuthenticationToken}
          onClose={onCloseReferenceAuthenticationModal}
        />
      ) : null}
      {downloadBlobData !== undefined && <DownloadBlobModal data={downloadBlobData} />}
      {isLandRegistrationModalOpen && <LandRegistrationModal />}
      {isDocumentDigitalRegistrationModalOpen && digitalRegistrationDocument != null && (
        <DigitalSignatureModal document={digitalRegistrationDocument} />
      )}
      {isDocumentSignatureMethodSelectionModalOpen && <DocumentSignatureMethodSelectionModal />}
    </Suspense>
  );

  function getVisibleOnLoadModal() {
    if (userDetailsData == null || !userDetailsData?.authenticated) {
      return;
    }

    if (
      referenceAuthenticationToken != null &&
      getStorageItem(`${REFERENCE_AUTH_MODAL_STORAGE_KEY}_${userDetailsData.id}`) == null
    ) {
      setVisibleOnLoadModal(OnLoadModals.REFERENCE_AUTHENTICATION);
      return;
    }

    if (!userDetailsData.conditionsAccepted) {
      setVisibleOnLoadModal(OnLoadModals.CONDITIONS);
    }
  }
};

export default Modals;
